import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ReportData,
  ExportReportInput,
  ExportResult,
  ExportFormat
} from '../types/report';
import { Review, ReviewCycle } from '../types/pms';
import { getRatingLabel } from './pmsRatingCalculator';
import { getReviewWorkflowStatusLabel, managerCommentOf } from './pmsWorkflow';
import { ReportService } from './report';

/**
 * Service for handling report export functionality
 * Requirements: 7.4 - CSV and PDF export with download functionality
 */
export class ReportExportService {

  /**
   * Exports a report in the specified format
   * Requirements: 7.4 - Report export with all expense data fields
   */
  static async exportReport(
    input: ExportReportInput, 
    reportData: ReportData
  ): Promise<ExportResult> {
    try {
      let fileBlob: Blob;
      let fileName: string;
      let fileSize: number;

      if (input.format === ExportFormat.CSV) {
        const csvContent = this.generateCSV(reportData, input.includeDetails || false);
        fileBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        fileName = this.generateFileName(reportData, 'csv');
      } else if (input.format === ExportFormat.PDF) {
        const pdfBlob = await this.generatePDF(reportData, input.includeDetails || false);
        fileBlob = pdfBlob;
        fileName = this.generateFileName(reportData, 'pdf');
      } else {
        throw new Error(`Unsupported export format: ${input.format}`);
      }

      fileSize = fileBlob.size;

      // Create object URL for download
      const fileUrl = URL.createObjectURL(fileBlob);

      // Create export result
      const exportResult: ExportResult = {
        id: this.generateExportId(),
        reportId: input.reportId,
        format: input.format,
        fileName,
        fileUrl,
        fileSize,
        exportedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
      };

      // Store the blob for later download
      this.storeExportFile(exportResult.id, fileBlob);

      return exportResult;
    } catch (error) {
      console.error('Failed to export report:', error);
      throw new Error(
        error instanceof Error 
          ? `Failed to export report: ${error.message}` 
          : 'Failed to export report'
      );
    }
  }

  /**
   * Generates CSV content from report data
   * Requirements: 7.4 - CSV export with all expense data fields
   */
  private static generateCSV(reportData: ReportData, includeDetails: boolean): string {
    const lines: string[] = [];
    
    // Add report header - always present for all reports
    lines.push(`"Report Title","${reportData.title}"`);
    lines.push(`"Generated At","${new Date(reportData.generatedAt).toLocaleString()}"`);
    lines.push(`"Report Type","${reportData.type}"`);
    lines.push(`"Total Expenses","${reportData.totalExpenses}"`);
    lines.push(`"Total Amount","${ReportService.formatAmount(reportData.totalAmount)}"`);
    lines.push(''); // Empty line

    // Add filters section - always present even if empty
    lines.push('"Applied Filters"');
    if (reportData.filters.dateFrom) {
      lines.push(`"Date From","${reportData.filters.dateFrom}"`);
    }
    if (reportData.filters.dateTo) {
      lines.push(`"Date To","${reportData.filters.dateTo}"`);
    }
    if (reportData.filters.department) {
      lines.push(`"Department","${reportData.filters.department}"`);
    }
    if (reportData.filters.employeeId) {
      lines.push(`"Employee ID","${reportData.filters.employeeId}"`);
    }
    if (reportData.filters.categories && reportData.filters.categories.length > 0) {
      lines.push(`"Categories","${reportData.filters.categories.join(', ')}"`);
    }
    if (reportData.filters.statuses && reportData.filters.statuses.length > 0) {
      lines.push(`"Statuses","${reportData.filters.statuses.join(', ')}"`);
    }
    if (reportData.filters.minAmount !== undefined) {
      lines.push(`"Min Amount","${ReportService.formatAmount(reportData.filters.minAmount)}"`);
    }
    if (reportData.filters.maxAmount !== undefined) {
      lines.push(`"Max Amount","${ReportService.formatAmount(reportData.filters.maxAmount)}"`);
    }
    // Add "No filters applied" message if no filters are set
    const hasFilters = reportData.filters.dateFrom || reportData.filters.dateTo || 
                      reportData.filters.department || reportData.filters.employeeId ||
                      (reportData.filters.categories && reportData.filters.categories.length > 0) ||
                      (reportData.filters.statuses && reportData.filters.statuses.length > 0) ||
                      reportData.filters.minAmount !== undefined || reportData.filters.maxAmount !== undefined;
    if (!hasFilters) {
      lines.push('"No filters applied"');
    }
    lines.push(''); // Empty line

    // Add summary section - always present
    lines.push('"Summary"');
    lines.push(`"Total Expenses","${reportData.summary.totalExpenses}"`);
    lines.push(`"Total Amount","${ReportService.formatAmount(reportData.summary.totalAmount)}"`);
    lines.push(`"Average Amount","${ReportService.formatAmount(reportData.summary.averageAmount)}"`);
    lines.push(''); // Empty line

    // Add category breakdown section - always include headers even if empty
    lines.push('"Category Breakdown"');
    lines.push('"Category","Count","Amount","Percentage"');
    if (reportData.summary.categoryBreakdown.length > 0) {
      reportData.summary.categoryBreakdown.forEach(item => {
        lines.push(`"${item.category}","${item.count}","${ReportService.formatAmount(item.amount)}","${ReportService.formatPercentage(item.percentage)}"`);
      });
    } else {
      lines.push('"No category data available"');
    }
    lines.push(''); // Empty line

    // Add status breakdown section - always include headers even if empty
    lines.push('"Status Breakdown"');
    lines.push('"Status","Count","Amount","Percentage"');
    if (reportData.summary.statusBreakdown.length > 0) {
      reportData.summary.statusBreakdown.forEach(item => {
        lines.push(`"${item.status}","${item.count}","${ReportService.formatAmount(item.amount)}","${ReportService.formatPercentage(item.percentage)}"`);
      });
    } else {
      lines.push('"No status data available"');
    }
    lines.push(''); // Empty line

    // Add monthly breakdown if available
    if (reportData.summary.monthlyBreakdown) {
      lines.push('"Monthly Breakdown"');
      lines.push('"Month","Count","Amount"');
      if (reportData.summary.monthlyBreakdown.length > 0) {
        reportData.summary.monthlyBreakdown.forEach(item => {
          lines.push(`"${item.month}","${item.count}","${ReportService.formatAmount(item.amount)}"`);
        });
      } else {
        lines.push('"No monthly data available"');
      }
      lines.push(''); // Empty line
    }

    // Add department breakdown if available
    if (reportData.summary.departmentBreakdown) {
      lines.push('"Department Breakdown"');
      lines.push('"Department","Count","Amount","Employee Count"');
      if (reportData.summary.departmentBreakdown.length > 0) {
        reportData.summary.departmentBreakdown.forEach(item => {
          lines.push(`"${item.department}","${item.count}","${ReportService.formatAmount(item.amount)}","${item.employeeCount}"`);
        });
      } else {
        lines.push('"No department data available"');
      }
      lines.push(''); // Empty line
    }

    // Add detailed expense data section - always include headers if requested
    if (includeDetails) {
      lines.push('"Detailed Expense Data"');
      lines.push('"Expense ID","Title","Category","Amount","Date","Status","Submitter","Department","Approval Date","Reimbursement Date"');
      if (reportData.details.length > 0) {
        reportData.details.forEach(detail => {
          const row = [
            `"${detail.expenseId}"`,
            `"${this.escapeCsvValue(detail.title)}"`,
            `"${detail.category}"`,
            `"${ReportService.formatAmount(detail.amount)}"`,
            `"${detail.date}"`,
            `"${detail.status}"`,
            `"${detail.submitterName}"`,
            `"${detail.submitterDepartment}"`,
            `"${detail.approvalDate || 'N/A'}"`,
            `"${detail.reimbursementDate || 'N/A'}"`
          ];
          lines.push(row.join(','));
        });
      } else {
        lines.push('"No expense details available"');
      }
    }

    return lines.join('\n');
  }

  /**
   * Generates PDF content from report data
   * Requirements: 7.4 - PDF report generation for summaries
   */
  private static async generatePDF(reportData: ReportData, includeDetails: boolean): Promise<Blob> {
    const doc = new jsPDF();
    let yPosition = 20;

    // Add title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(reportData.title, 20, yPosition);
    yPosition += 15;

    // Add metadata
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date(reportData.generatedAt).toLocaleString()}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Report Type: ${reportData.type}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Total Expenses: ${reportData.totalExpenses}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Total Amount: ${ReportService.formatAmount(reportData.totalAmount)}`, 20, yPosition);
    yPosition += 15;

    // Add filters if any
    if (Object.keys(reportData.filters).length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Applied Filters', 20, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      if (reportData.filters.dateFrom || reportData.filters.dateTo) {
        const dateRange = ReportService.formatDateRange(reportData.filters.dateFrom, reportData.filters.dateTo);
        doc.text(`Date Range: ${dateRange}`, 20, yPosition);
        yPosition += 6;
      }
      
      if (reportData.filters.department) {
        doc.text(`Department: ${reportData.filters.department}`, 20, yPosition);
        yPosition += 6;
      }
      
      if (reportData.filters.categories && reportData.filters.categories.length > 0) {
        doc.text(`Categories: ${reportData.filters.categories.join(', ')}`, 20, yPosition);
        yPosition += 6;
      }
      
      yPosition += 10;
    }

    // Add summary section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 20, yPosition);
    yPosition += 10;

    const summaryData = [
      ['Total Expenses', reportData.summary.totalExpenses.toString()],
      ['Total Amount', ReportService.formatAmount(reportData.summary.totalAmount)],
      ['Average Amount', ReportService.formatAmount(reportData.summary.averageAmount)]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: 20, right: 20 }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 15;

    // Add category breakdown
    if (reportData.summary.categoryBreakdown.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Category Breakdown', 20, yPosition);
      yPosition += 10;

      const categoryData = reportData.summary.categoryBreakdown.map(item => [
        item.category,
        item.count.toString(),
        ReportService.formatAmount(item.amount),
        ReportService.formatPercentage(item.percentage)
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Category', 'Count', 'Amount', 'Percentage']],
        body: categoryData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 }
      });

      yPosition = (doc as any).lastAutoTable.finalY + 15;
    }

    // Add status breakdown
    if (reportData.summary.statusBreakdown.length > 0) {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Status Breakdown', 20, yPosition);
      yPosition += 10;

      const statusData = reportData.summary.statusBreakdown.map(item => [
        item.status,
        item.count.toString(),
        ReportService.formatAmount(item.amount),
        ReportService.formatPercentage(item.percentage)
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Status', 'Count', 'Amount', 'Percentage']],
        body: statusData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 }
      });

      yPosition = (doc as any).lastAutoTable.finalY + 15;
    }

    // Add detailed expense data if requested
    if (includeDetails && reportData.details.length > 0) {
      doc.addPage();
      yPosition = 20;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Detailed Expense Data', 20, yPosition);
      yPosition += 10;

      const detailsData = reportData.details.map(detail => [
        detail.title.length > 20 ? detail.title.substring(0, 20) + '...' : detail.title,
        detail.category,
        ReportService.formatAmount(detail.amount),
        detail.date,
        detail.status,
        detail.submitterName.length > 15 ? detail.submitterName.substring(0, 15) + '...' : detail.submitterName
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [['Title', 'Category', 'Amount', 'Date', 'Status', 'Submitter']],
        body: detailsData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 },
        styles: { fontSize: 8 }
      });
    }

    return doc.output('blob');
  }

  /**
   * Downloads a file from URL
   * Requirements: 7.4 - Download functionality
   */
  static downloadFile(fileUrl: string, fileName: string): void {
    try {
      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download file:', error);
      // Fallback to saveAs
      fetch(fileUrl)
        .then(response => response.blob())
        .then(blob => saveAs(blob, fileName))
        .catch(err => {
          console.error('Fallback download failed:', err);
          throw new Error('Failed to download file');
        });
    }
  }

  /**
   * Stores export file for later retrieval
   * Requirements: 7.4 - Export history and file management
   */
  private static storeExportFile(exportId: string, fileBlob: Blob): void {
    // In a real implementation, this would upload to S3 or similar storage
    // For now, we'll store in memory with the object URL
    const exportFiles = this.getStoredExports();
    exportFiles.set(exportId, {
      blob: fileBlob,
      createdAt: Date.now()
    });
    
    // Clean up old exports (older than 24 hours)
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    for (const [id, data] of exportFiles.entries()) {
      if (data.createdAt < oneDayAgo) {
        exportFiles.delete(id);
      }
    }
  }

  /**
   * Gets stored exports from memory
   */
  private static getStoredExports(): Map<string, { blob: Blob; createdAt: number }> {
    if (!(window as any).__exportFiles) {
      (window as any).__exportFiles = new Map();
    }
    return (window as any).__exportFiles;
  }

  /**
   * Generates unique export ID
   */
  private static generateExportId(): string {
    return `export_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generates filename for export
   * Requirements: 7.4 - Proper file naming
   */
  private static generateFileName(reportData: ReportData, extension: string): string {
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const reportType = reportData.type.toLowerCase().replace(/\s+/g, '-');
    return `${reportType}-report-${timestamp}.${extension}`;
  }

  /**
   * Escapes CSV values to handle commas and quotes
   */
  private static escapeCsvValue(value: string): string {
    if (value.includes('"')) {
      return value.replace(/"/g, '""');
    }
    return value;
  }

  /**
   * Gets export history for a user
   * Requirements: 7.4 - Export history tracking
   */
  static getExportHistory(): ExportResult[] {
    // In a real implementation, this would fetch from a database
    // For now, return empty array as history is managed in component state
    return [];
  }

  /**
   * Cleans up expired exports
   * Requirements: 7.4 - File management
   */
  static cleanupExpiredExports(): void {
    const exportFiles = this.getStoredExports();
    const now = Date.now();
    
    for (const [id, data] of exportFiles.entries()) {
      const expiryTime = data.createdAt + 24 * 60 * 60 * 1000; // 24 hours
      if (now > expiryTime) {
        exportFiles.delete(id);
      }
    }
  }

  /** Export a single PMS review summary as PDF */
  static exportReviewSummaryPDF(review: Review, cycle?: ReviewCycle): void {
    const doc = new jsPDF();
    const cycleName = cycle?.name || review.reviewCycleId;
    const reviewType = cycle?.reviewCycleType
      ? (cycle.reviewCycleType === 'YEAR_END' ? 'Year End Review' : 'High Level Review')
      : review.reviewType || 'Performance Review';

    doc.setFontSize(16);
    doc.text('Performance Review Summary', 14, 18);
    doc.setFontSize(10);
    doc.text(`Employee: ${review.employeeName} (${review.employeeId})`, 14, 28);
    doc.text(`Manager: ${review.reviewerName}`, 14, 34);
    doc.text(`Review Cycle: ${cycleName}`, 14, 40);
    doc.text(`Review Type: ${reviewType}`, 14, 46);
    doc.text(`Status: ${getReviewWorkflowStatusLabel(review.workflowStatus)}`, 14, 52);
    doc.text(`Acknowledgement: ${review.isAcknowledged ? `Acknowledged on ${review.acknowledgedAt || ''}` : 'Pending Acknowledgement'}`, 14, 58);

    const rows = review.objectiveRatings.map((or) => [
      or.objectiveTitle,
      or.closureStatus || or.evaluationSummary || '',
      `${or.finalProgressPercentage ?? '—'}%`,
      or.rating ? getRatingLabel(or.rating) : '',
      // Blank until the manager evaluates — the PDF must not print the employee's own
      // self-review note in a column headed "Manager Comment".
      managerCommentOf(or, review),
    ]);
    autoTable(doc, {
      startY: 66,
      head: [['Objective', 'Evaluation Summary', 'Progress', 'Rating', 'Manager Comment']],
      body: rows,
      styles: { fontSize: 8 },
    });

    let finalY = (doc as any).lastAutoTable?.finalY || 120;
    doc.text(`Overall Rating: ${review.overallRating ? getRatingLabel(review.overallRating) : 'N/A'}`, 14, finalY + 12);
    doc.text('Overall Comments:', 14, finalY + 20);
    doc.text(doc.splitTextToSize(review.overallComments || 'None', 180), 14, finalY + 26);
    doc.text('Development Suggestions:', 14, finalY + 40);
    doc.text(doc.splitTextToSize(review.developmentSuggestions || 'None', 180), 14, finalY + 46);

    // Audit Timeline
    const history = review.reviewHistory || review.reviewMetadata?.reviewHistory || [];
    if (history.length > 0) {
      finalY = finalY + 60;
      if (finalY > 250) { doc.addPage(); finalY = 20; }
      doc.setFontSize(12);
      doc.text('Audit Timeline', 14, finalY);
      doc.setFontSize(9);
      const timelineRows = history.map((entry: any) => [
        entry.action || '',
        entry.performedByName || entry.performedBy || '',
        entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '',
        entry.newValue || '',
      ]);
      autoTable(doc, {
        startY: finalY + 6,
        head: [['Action', 'User', 'Date/Time', 'Details']],
        body: timelineRows,
        styles: { fontSize: 7 },
      });
    }

    doc.save(`review-summary-${review.employeeName.replace(/\s+/g, '-')}.pdf`);
  }

  /** Export review summaries for HR as CSV */
  static exportReviewsCSV(reviews: Review[], cycles: ReviewCycle[]): void {
    const cycleMap = new Map(cycles.map((c) => [c.id, c]));
    const header = [
      'Employee',
      'Manager',
      'Review Cycle',
      'Review Type',
      'Status',
      'Overall Rating',
      'Development Suggestions',
      'Submitted At',
      'Acknowledgement Status',
      'Acknowledged At',
    ];
    const lines = [header.join(',')];
    for (const review of reviews) {
      const cycle = cycleMap.get(review.reviewCycleId);
      const reviewType = cycle?.reviewCycleType === 'YEAR_END' ? 'Year End Review'
        : cycle?.reviewCycleType === 'HIGH_LEVEL' ? 'High Level Review'
        : review.reviewType || '';
      lines.push([
        `"${review.employeeName}"`,
        `"${review.reviewerName}"`,
        `"${cycle?.name || review.reviewCycleId}"`,
        `"${reviewType}"`,
        `"${getReviewWorkflowStatusLabel(review.workflowStatus)}"`,
        review.overallRating || '',
        `"${(review.developmentSuggestions || '').replace(/"/g, '""')}"`,
        review.submittedAt || review.updatedAt || '',
        review.isAcknowledged ? 'Acknowledged' : 'Pending',
        review.acknowledgedAt || '',
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `pms-reviews-export-${new Date().toISOString().slice(0, 10)}.csv`);
  }
}